import { Drawer } from '@blueprintjs/core'
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import dayjs from 'dayjs'
import { sortBy } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type {
  WellCulturePastImageObservation,
  WellPage_PastObservation,
  WellPage_WellCulture,
} from '~/api/backend/pages/wellPageAPI'
import { queryClient } from '~/api/query-client'
import { QUERY_KEYS } from '~/api/query-client-keys'
import type { BackendHTTPError } from '~/api/shared/BackendHTTPError'
import Breadcrumbs from '~/components/Breadcrumbs'
import { FullPageError } from '~/components/Errors'
import {
  DEFAULT_FILTERS,
  type ImageFilters,
} from '~/components/ImageManipulationControls'
import MetadataSidebar, { MetadataItem } from '~/components/MetadataSidebar'
import MontageHero, { MontageHeroLoadingPlaceholder } from '~/components/MontageHero'
import PageNotFound from '~/components/PageNotFound'
import TinyMicroplate from '~/components/TinyMicroplate'
import {
  SUPPORTED_PLATE_DIMENSIONS,
  type supportedPlateFormats,
} from '~/components/TinyMicroplate.interface'
import { Skeleton } from '~/components/ui/skeleton'
import { WellCellConcentrationGraph } from '~/components/WellCellConcentrationGraph'
import { analytics } from '~/core/analytics'
import { useFeatureFlag } from '~/core/featureFlags'
import { useDemoQuery } from '~/demoControls/DemoContext'
import { cn } from '~/lib/utils'
import CultureStatusPicker from '~/pages/Monitor/components/CultureStatusPicker'
import { useMetrics } from '~/pages/Monitor/metrics'
import MontageImagesDialog from '~/pages/Monitor/pages/MontageImagesDialog/MontageImagesDialog'
import { decodeURITimestamp, encodeURITimestamp } from '~/utils/date'
import {
  convertWellCoordsToWellName,
  convertWellNameToWellCoord,
} from '~/utils/microplate'
import CultureLineage from './CultureLineage/CultureLineage'
import LateralNavCompass, {
  LateralNavAxisMode,
  LateralNavDirection,
} from './LateralNavCompass'
import {
  usePrefetchWellOnHover,
  usePreloadAdjacentObservations,
  usePreloadAdjacentWells,
} from './useWellPagePreloading'
import WellTabbedSidebar from './WellTabbedSidebar'
import cs from './well_page.scss'

function generateZoomAndPanStorageKey(plateID: string, well: string) {
  return `zoom_and_pan_${plateID}_${well}`
}

export default function WellPage({ plateID, pos }: { plateID: string; pos: string }) {
  const variables = { plateId: plateID, position: pos }
  const demoData = useDemoQuery('monitor', 'WellPage')?.(variables)
  const client = useBackendClient()

  const { data, isLoading, error } = useQuery({
    queryFn: async () =>
      await client.wellPage.getData({
        plate_query_id: plateID,
        well: pos,
      }),
    queryKey: [QUERY_KEYS.WELL_PAGE, plateID, pos],
    // Match staleTime with prefetch in useWellPagePreloading.ts to ensure
    // prefetched data is used instead of making duplicate requests
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: demoData == null,
  })

  if (demoData?.culture) {
    return <Content culture={demoData.culture} usingDemoData={true} plateID={plateID} />
  }

  if (isLoading) {
    return <LoadingPlaceholder plateID={plateID} pos={pos} />
  }

  if (error) {
    return <FullPageError error={error as BackendHTTPError} />
  }

  const culture = data?.culture

  if (culture == null) {
    return <PageNotFound />
  }

  return (
    <Content
      culture={culture}
      usingDemoData={false}
      plateID={plateID}
      key={plateID + culture.well}
    />
  )
}

// Interface for zoom/pan state
interface ZoomPanState {
  scale: number
  positionX: number
  positionY: number
}

function Content({
  culture,
  usingDemoData,
  plateID,
}: {
  culture: WellPage_WellCulture
  plateID: string
  usingDemoData?: boolean
}) {
  const history = useHistory()
  const client = useBackendClient()
  const panAndZoom = useFeatureFlag('panAndZoom')
  const cultureLineageEnabled = useFeatureFlag('cultureLineage')
  const hackathon2025 = useFeatureFlag('hackathon2025')
  const metrics = useMetrics()

  // The index of the image the user clicked, or null if the image dialog is closed.
  // Note: This doesn't update when switching images within the dialog.
  const [openedImage, setOpenedImage] = useState<number | null>(null)

  // Store viewport dimensions for coordinate normalization
  const [viewportDimensions, setViewportDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  // Callback for viewport dimensions
  const handleViewportDimensionsChange = useCallback(
    (dimensions: { width: number; height: number }) => {
      setViewportDimensions(dimensions)
    },
    [],
  )

  // Get URL params early so we can use them in state initialization
  const location = useLocation()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])

  const currentWellStorageKey = useMemo(
    () => generateZoomAndPanStorageKey(plateID, culture.well),
    [plateID, culture.well],
  )

  // Put near the ZoomPanState type
  const DEFAULT_ZOOM_PAN_STATE: ZoomPanState = {
    scale: 1,
    positionX: 0,
    positionY: 0,
  }

  // Check if we have zoom/pan params in URL
  const hasZoomPanUrlParams =
    params.has('scale') ||
    params.has('normX') ||
    params.has('normY') ||
    params.has('posX') ||
    params.has('posY')

  // Initial state: URL params > localStorage > default
  const [zoomPanState, setZoomPanState] = useState<ZoomPanState>(() => {
    // If URL has zoom/pan params, start with defaults and let the effect apply them later
    // This ensures URL params take priority over localStorage
    if (hasZoomPanUrlParams) {
      // For legacy absolute coordinates, we can apply them immediately
      const posX = params.get('posX')
      const posY = params.get('posY')
      const scale = params.get('scale')

      if (scale && posX && posY) {
        return {
          scale: Number.parseFloat(scale),
          positionX: Number.parseFloat(posX),
          positionY: Number.parseFloat(posY),
        }
      }
      // For normalized coordinates, we need to wait for viewport dimensions
      return DEFAULT_ZOOM_PAN_STATE
    }

    // No URL params, check localStorage
    const stored = localStorage.getItem(currentWellStorageKey)
    return stored ? JSON.parse(stored) : DEFAULT_ZOOM_PAN_STATE
  })

  const handleZoomPanChange = useCallback(
    (state: ZoomPanState) => {
      setZoomPanState(state)
      localStorage.setItem(currentWellStorageKey, JSON.stringify(state))
      metrics.well.zoomPan(culture.id)

      // Clear URL params on interaction - they're only meant as initial state
      const currentParams = new URLSearchParams(location.search)
      if (
        currentParams.has('scale') ||
        currentParams.has('normX') ||
        currentParams.has('normY') ||
        currentParams.has('posX') ||
        currentParams.has('posY')
      ) {
        currentParams.delete('scale')
        currentParams.delete('normX')
        currentParams.delete('normY')
        currentParams.delete('posX')
        currentParams.delete('posY')
        history.replace({ search: currentParams.toString() })
      }
    },
    [currentWellStorageKey, location.search, history, culture.id, metrics],
  )

  // Track if we've already applied URL params to avoid re-applying on dimension changes
  const [urlParamsApplied, setUrlParamsApplied] = useState(false)

  // When viewport dimensions become available, recalculate position from normalized URL params (only once)
  useEffect(() => {
    if (
      !viewportDimensions ||
      viewportDimensions.width === 0 ||
      viewportDimensions.height === 0 ||
      urlParamsApplied ||
      !hasZoomPanUrlParams // Only apply if we actually have URL params
    )
      return

    // Only apply if we have normalized coordinates in the URL
    const normX = params.get('normX')
    const normY = params.get('normY')
    const scale = params.get('scale')

    if (normX && normY && scale) {
      const newState = {
        scale: Number.parseFloat(scale),
        positionX: Number.parseFloat(normX) * viewportDimensions.width,
        positionY: Number.parseFloat(normY) * viewportDimensions.height,
      }
      setZoomPanState(newState)
      // Also save to localStorage since this is now the active state
      localStorage.setItem(currentWellStorageKey, JSON.stringify(newState))
      setUrlParamsApplied(true)
    }
  }, [
    viewportDimensions,
    params,
    urlParamsApplied,
    hasZoomPanUrlParams,
    currentWellStorageKey,
  ])

  const sortedObservationHistory = useMemo(
    () => sortBy(culture.observationHistory, 'generated_at'),
    [culture.observationHistory],
  )
  // Find the observation specified in the URL, defaulting to the latest one if
  // not specified.

  // Culture lineage state
  const lineageInitiallyOpen = params.get('lineage') === 'show'
  const [cultureLineageOpen, setCultureLineageOpen] = useState(lineageInitiallyOpen)
  const decodedTime = decodeURITimestamp(params.get('t'))
  const decodedTimeISOString = decodedTime?.toISOString()
  let observationIndex = decodedTimeISOString
    ? sortedObservationHistory.findIndex(
        obs => obs.generated_at >= decodedTimeISOString,
      )
    : -1
  if (observationIndex < 0) {
    observationIndex = sortedObservationHistory.length - 1
  }
  const observation = sortedObservationHistory[observationIndex] as
    | WellCulturePastImageObservation
    | undefined

  useEffect(() => {
    analytics.page('Monitor', 'Well', {
      cultureId: culture.id,
      numCultureObservations: culture.observationHistory.length,
      observationTimestamp: observation?.generated_at,
    })
  }, [culture.id, observation?.generated_at, culture.observationHistory.length])

  // Canonicalize the timestamp in the URL to match the actual observation.
  // Only runs once per well visit to avoid interfering with navigation.
  const hasCanonicalizedTimestamp = useRef(false)

  useEffect(() => {
    // Only canonicalize once per well visit
    if (hasCanonicalizedTimestamp.current) {
      return
    }

    // If there's no timestamp param at all, avoid an unnecessary replace.
    if (!params.has('t')) {
      hasCanonicalizedTimestamp.current = true
      return
    }

    const canonicalObservationTimestamp = observation
      ? encodeURITimestamp(dayjs(observation.generated_at))
      : ''

    if (params.get('t') !== canonicalObservationTimestamp) {
      const newParams = new URLSearchParams(params)
      if (decodedTime) {
        newParams.set('t', canonicalObservationTimestamp)
      } else {
        newParams.delete('t')
      }
      history.replace({ search: newParams.toString() })
    }

    hasCanonicalizedTimestamp.current = true
  }, [observation, decodedTime, params, history])

  const plateDims = culture.culturePlate.plateDimensions
  const tinyMicroplateFormat = SUPPORTED_PLATE_DIMENSIONS.some(
    ({ rows, columns }) => rows === plateDims.rows && columns === plateDims.columns,
  )
    ? (`wells_${plateDims.rows * plateDims.columns}` as supportedPlateFormats)
    : null

  const [stitchImage, setStitchImage] = useState(panAndZoom)

  // Helper function to parse image filters from URL
  const getFiltersFromURL = useCallback(
    (searchParams: URLSearchParams): ImageFilters | null => {
      const brightness = searchParams.get('brightness')
      const contrast = searchParams.get('contrast')

      if (brightness || contrast) {
        return {
          ...DEFAULT_FILTERS,
          brightness: brightness
            ? Number.parseFloat(brightness)
            : DEFAULT_FILTERS.brightness,
          contrast: contrast ? Number.parseFloat(contrast) : DEFAULT_FILTERS.contrast,
        }
      }
      return null
    },
    [],
  )

  // Storage key for filters
  const filtersStorageKey = useMemo(
    () => `image_filters_${plateID}_${culture.well}`,
    [plateID, culture.well],
  )

  // Check if we have filter params in URL
  const hasFilterUrlParams = params.has('brightness') || params.has('contrast')

  // Initial state: URL params > localStorage > default
  const [imageFilters, setImageFilters] = useState<ImageFilters>(() => {
    // If URL has filter params, use them (priority over localStorage)
    if (hasFilterUrlParams) {
      const urlFilters = getFiltersFromURL(params)
      return urlFilters ?? DEFAULT_FILTERS
    }

    // No URL params, check localStorage
    const stored = localStorage.getItem(filtersStorageKey)
    return stored ? JSON.parse(stored) : DEFAULT_FILTERS
  })

  // Handler for filter changes that also clears URL params and saves to localStorage
  const handleImageFiltersChange = useCallback(
    (filters: ImageFilters) => {
      setImageFilters(filters)
      localStorage.setItem(filtersStorageKey, JSON.stringify(filters))

      // Clear URL params on interaction - they're only meant as initial state
      const currentParams = new URLSearchParams(location.search)
      if (currentParams.has('brightness') || currentParams.has('contrast')) {
        currentParams.delete('brightness')
        currentParams.delete('contrast')
        history.replace({ search: currentParams.toString() })
      }
    },
    [location.search, history, filtersStorageKey],
  )

  // Track image loading state to defer expensive API calls
  const [imagesLoaded, setImagesLoaded] = useState(false)
  // If lineage is initially open, load immediately; otherwise defer until images load
  const [shouldLoadLineage, setShouldLoadLineage] = useState(lineageInitiallyOpen)

  // Sync drawer and API loading state with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search)
    const lineageParam = currentParams.get('lineage') === 'show'

    // Update drawer state to match URL
    setCultureLineageOpen(lineageParam)

    // If lineage=show in URL, trigger API load immediately
    // If not, don't modify shouldLoadLineage (let deferral logic handle it)
    if (lineageParam) {
      setShouldLoadLineage(true)
    }
  }, [location.search])

  // Handle image loading state changes from MontageHero
  const handleImageLoadingChange = useCallback((isLoading: boolean) => {
    if (!isLoading) {
      setImagesLoaded(prevLoaded => {
        if (!prevLoaded) return true
        return prevLoaded
      })
    } else {
      // Images started loading again (e.g., navigation to new observation)
      // Reset lineage loading state before resetting image loaded state
      setImagesLoaded(prevLoaded => {
        if (prevLoaded) {
          // Side effect: reset lineage loading when transitioning from loaded to loading
          setShouldLoadLineage(false)
          return false
        }
        return prevLoaded
      })
    }
  }, [])

  // Defer lineage loading until 500ms after images finish loading
  // This avoids competing for bandwidth/resources during initial page render
  // Skip deferral if lineage drawer is initially open (user navigated directly)
  useEffect(() => {
    if (!imagesLoaded || shouldLoadLineage || lineageInitiallyOpen) return

    const timer = setTimeout(() => {
      setShouldLoadLineage(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [imagesLoaded, shouldLoadLineage, lineageInitiallyOpen])

  // Culture lineage hook - deferred until images are loaded
  const { data: cultureLineageData } = useQuery({
    queryFn: async () => await client.wellPage.getCultureLineage(culture.id),
    queryKey: [QUERY_KEYS.CULTURE_LINEAGE, culture.id],
    enabled:
      !usingDemoData &&
      culture.id != null &&
      cultureLineageEnabled &&
      shouldLoadLineage,
  })

  const openCulture = useCallback(
    (plateId: string, well: string, timestamp: string) => {
      history.push(`/plate/${plateId}/well/${well}?t=${timestamp}&lineage=show`)
    },
    [history],
  )

  const lineageCultures = cultureLineageData?.cultures ?? []

  // Memoize available wells to prevent infinite re-renders.
  // Without memoization, .map() creates a new array on every render, which triggers
  // useWellNavOrdering to recreate the nextWellFor function, which triggers
  // usePreloadAdjacentWells to recalculate adjacentWells, causing an infinite loop.
  const availableWells = useMemo(
    () => culture.culturePlate.wellCultures.map(c => c.well),
    [culture.culturePlate.wellCultures],
  )

  // Calculate navigation ordering for preloading
  const nextWellFor = useWellNavOrdering(culture.well, availableWells)

  // Preload adjacent observations first (HIGH PRIORITY - users navigate time > space)
  // Starts immediately when current page images finish loading
  usePreloadAdjacentObservations(
    sortedObservationHistory,
    observationIndex,
    imagesLoaded,
  )

  // Preload adjacent wells second (LOWER PRIORITY - less frequent navigation)
  // APIs prefetch immediately, images wait 1s to avoid bandwidth competition
  usePreloadAdjacentWells(plateID, culture.well, nextWellFor, imagesLoaded)

  // Hook for hover-based prefetching
  const prefetchWellOnHover = usePrefetchWellOnHover(plateID)

  // Handler for sharing URL with current state (copies to clipboard, doesn't modify browser URL)
  const handleShareURL = useCallback(async () => {
    // Start with current URL params to preserve existing query parameters
    const shareParams = new URLSearchParams(params)

    // Add zoom/pan state to URL (only if in stitched view)
    if (stitchImage) {
      shareParams.set('scale', zoomPanState.scale.toString())

      // Try to get dimensions if not already available
      let effectiveDimensions = viewportDimensions

      // Fallback: Try to measure container directly if dimensions not available
      if (
        !effectiveDimensions ||
        effectiveDimensions.width === 0 ||
        effectiveDimensions.height === 0
      ) {
        const container = document.querySelector(
          '.react-transform-wrapper',
        ) as HTMLElement
        if (container) {
          const rect = container.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            effectiveDimensions = { width: rect.width, height: rect.height }
          }
        }
      }

      // Use normalized coordinates if viewport dimensions are available
      if (
        effectiveDimensions &&
        effectiveDimensions.width > 0 &&
        effectiveDimensions.height > 0
      ) {
        // Store normalized coordinates (0-1 range) for portability across screen sizes
        const normalizedX = zoomPanState.positionX / effectiveDimensions.width
        const normalizedY = zoomPanState.positionY / effectiveDimensions.height

        shareParams.set('normX', normalizedX.toString())
        shareParams.set('normY', normalizedY.toString())

        // Remove legacy absolute position params if they exist
        shareParams.delete('posX')
        shareParams.delete('posY')
      } else {
        // Fallback to absolute positions if dimensions not available
        shareParams.set('posX', zoomPanState.positionX.toString())
        shareParams.set('posY', zoomPanState.positionY.toString())
      }
    }

    // Add filter state to URL (only if different from defaults)
    if (imageFilters.brightness !== DEFAULT_FILTERS.brightness) {
      shareParams.set('brightness', imageFilters.brightness.toString())
    }
    if (imageFilters.contrast !== DEFAULT_FILTERS.contrast) {
      shareParams.set('contrast', imageFilters.contrast.toString())
    }

    // Build full URL for sharing (doesn't modify browser URL)
    const shareURL = `${window.location.origin}${window.location.pathname}?${shareParams.toString()}`

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareURL)
      metrics.well.shareUrl(culture.id)
      return true
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error)
      return false
    }
  }, [
    params,
    stitchImage,
    zoomPanState,
    imageFilters,
    viewportDimensions,
    metrics,
    culture.id,
  ])

  // Handler for resetting filters and clearing URL params
  const handleResetFilters = useCallback(() => {
    // Reset filter state and localStorage
    setImageFilters(DEFAULT_FILTERS)
    localStorage.setItem(filtersStorageKey, JSON.stringify(DEFAULT_FILTERS))

    // Reset zoom/pan state and localStorage
    setZoomPanState(DEFAULT_ZOOM_PAN_STATE)
    localStorage.setItem(currentWellStorageKey, JSON.stringify(DEFAULT_ZOOM_PAN_STATE))

    // Clear URL params related to image manipulation
    const newParams = new URLSearchParams(params)
    newParams.delete('scale')
    newParams.delete('normX')
    newParams.delete('normY')
    newParams.delete('posX')
    newParams.delete('posY')
    newParams.delete('brightness')
    newParams.delete('contrast')

    // Reset the flag so URL params can be applied again if needed
    setUrlParamsApplied(false)

    history.replace({ search: newParams.toString() })
  }, [currentWellStorageKey, filtersStorageKey, params, history])

  return (
    <div className='w-full h-full flex flex-col'>
      {/* Top Bar with Breadcrumbs */}
      <div className='font-text h-[52px] border-b border-grey-lightest px-10 flex items-center shrink-0'>
        <Breadcrumbs
          plate={{ id: culture.culturePlate.id, data: culture.culturePlate }}
          wellPos={culture.well}
        />
      </div>

      {/* Main Content Area */}
      <div className='flex gap-6 px-10 h-[calc(100vh-52px)] overflow-hidden'>
        {/* Left Column - Hero and Controls */}
        <div className='flex flex-col flex-1 min-h-0'>
          {/* Title Bar and Toolbar Container - aligned with image */}
          <div className='w-full max-w-[calc(100vh-190px)] xl:max-w-[calc(100vh-200px)] 2xl:max-w-[calc(100vh-220px)]'>
            {/* Title Bar and Toolbar */}
            <div className='flex flex-col gap-4 shrink-0'>
              {/* Title Bar */}
              <div className='flex items-center justify-between h-[50px] mt-5'>
                <div className='flex flex-col gap-2'>
                  <div className='flex items-center gap-6'>
                    <span className='font-title text-grey-dark'>
                      Well {culture.well}
                    </span>

                    <div>
                      <CultureStatusPicker
                        currentStatus={culture.custom_culture_status}
                        onChange={async newStatus => {
                          if (usingDemoData) {
                            // TODO(nate): Support demo data
                            return
                          }
                          metrics.well.cultureStatusChange(
                            culture.id,
                            culture.custom_culture_status.name,
                            newStatus.name,
                          )
                          await client.cultureOperations.updateCultureStatus({
                            culture_extid: culture.id,
                            status_extid: newStatus.id,
                            // Update one well for now only. Support many wells later.
                            wells: [culture.well],
                          })
                          await Promise.all([
                            queryClient.invalidateQueries({
                              queryKey: [QUERY_KEYS.WELL_PAGE],
                            }),
                            queryClient.invalidateQueries({
                              queryKey: [QUERY_KEYS.PLATE_PAGE],
                            }),
                          ])
                        }}
                      />
                    </div>

                    {culture.custom_culture_status.is_archived && (
                      <span className='ml-3 text-grey-dark font-subtext font-semibold'>
                        {culture.status} (archived)
                      </span>
                    )}
                  </div>
                </div>

                <LateralNavControls
                  currentWell={culture.well}
                  availableWells={availableWells}
                  tinyMicroplateFormat={tinyMicroplateFormat}
                  onNavigate={toWell => {
                    if (toWell !== culture.well) {
                      metrics.well.wellNavigate(
                        culture.culturePlate.id,
                        culture.well,
                        toWell,
                      )
                      history.push(
                        `/plate/${culture.culturePlate.id}/well/${toWell}?${params.toString()}`,
                      )
                    }
                  }}
                  onHoverWell={prefetchWellOnHover}
                />
              </div>

              {/* Hero Toolbar */}
              <div className='h-[18px] mb-2.25'>
                <HeroToolbar
                  observation={observation}
                  page={{
                    number: observationIndex + 1,
                    total: sortedObservationHistory.length,
                  }}
                  onNavigate={dir => {
                    let newIndex: number
                    if (dir === LateralNavDirection.LEFT) {
                      newIndex = Math.max(observationIndex - 1, 0)
                      metrics.well.observationNavigate(culture.id, 'prev')
                    } else {
                      newIndex = Math.min(
                        observationIndex + 1,
                        sortedObservationHistory.length - 1,
                      )
                      metrics.well.observationNavigate(culture.id, 'next')
                    }
                    const newTimestamp = sortedObservationHistory[newIndex].generated_at
                    const newParams = new URLSearchParams(params)
                    newParams.set('t', encodeURITimestamp(dayjs(newTimestamp)))
                    // Don't use startTransition here - observation navigation within
                    // the same well should be immediate since data is already loaded
                    history.replace({ search: newParams.toString() })
                  }}
                />
              </div>
            </div>
          </div>

          {/* Hero/Montage */}
          <div className='w-full max-w-[calc(100vh-190px)] xl:max-w-[calc(100vh-200px)] 2xl:max-w-[calc(100vh-220px)] aspect-square'>
            {!hackathon2025 && (
              <MontageHero
                culture={culture}
                observationTimestamp={observation?.timestamp}
                onClickImage={
                  observation?.montage
                    ? (idx: number) => {
                        setOpenedImage(idx)
                      }
                    : undefined
                }
                zoomPanState={zoomPanState}
                onZoomPanChange={handleZoomPanChange}
                stitchImage={stitchImage}
                imageFilters={imageFilters}
                onLoadingStateChange={handleImageLoadingChange}
                onViewportDimensionsChange={handleViewportDimensionsChange}
              />
            )}
            {hackathon2025 && (
              <WellCellConcentrationGraph
                key={culture.well}
                wellCulture={culture}
                selectedTimestamp={
                  decodedTime && observation
                    ? dayjs(observation.timestamp).unix()
                    : undefined
                }
                onSelectObservation={(unixSeconds: number) => {
                  // Update the URL param 't' with the clicked timestamp to drive selected observation
                  const newParams = new URLSearchParams(params)
                  newParams.set('t', encodeURITimestamp(dayjs.unix(unixSeconds)))
                  history.replace({ search: newParams.toString() })
                }}
                onClearSelection={() => {
                  const newParams = new URLSearchParams(params)
                  newParams.delete('t')
                  history.replace({ search: newParams.toString() })
                }}
              />
            )}
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className={cn('w-[360px] xl:w-[540px] 2xl:w-[640px] shrink-0 pt-[80px]')}>
          <WellTabbedSidebar
            culture={culture}
            panAndZoom={panAndZoom ?? false}
            stitchImage={stitchImage ?? false}
            imageFilters={imageFilters}
            onImageFiltersChange={handleImageFiltersChange}
            onResetFilters={handleResetFilters}
            onShareURL={handleShareURL}
            cultureLineageEnabled={cultureLineageEnabled}
            onViewLineageClick={() => {
              const newParams = new URLSearchParams(params)
              newParams.set('lineage', 'show')
              history.replace({ search: newParams.toString() })
              setCultureLineageOpen(true)
              // Trigger lineage API load immediately when user opens drawer
              setShouldLoadLineage(true)
              metrics.well.lineageOpen(culture.id)
            }}
            onStitchImageChange={setStitchImage}
            observationNumber={observationIndex + 1}
            observationId={observation?.extid}
            zoomPanState={zoomPanState}
          />
        </div>
      </div>

      {observation?.montage && openedImage != null ? (
        // Normally we'd keep this mounted and use the `isOpen` prop to control
        // its visibility. Here, as a hack, we unmount it when closed, so that
        // it always resets to the `initialImageIndex` when opened.
        <MontageImagesDialog
          montage={observation.montage}
          isOpen
          onClose={() => {
            setOpenedImage(null)
          }}
          initialImageIndex={openedImage ?? undefined}
          linkToCulture={false}
        />
      ) : null}

      {/* Culture Lineage Component */}
      {cultureLineageEnabled && (
        <Drawer
          isOpen={cultureLineageOpen}
          onClose={() => {
            const newParams = new URLSearchParams(params)
            newParams.delete('lineage')
            history.replace({ search: newParams.toString() })
            setCultureLineageOpen(false)
          }}
          position='bottom'
          hasBackdrop={false}
          size='360px'
        >
          <CultureLineage
            isOpen={cultureLineageOpen}
            onClose={() => {
              const newParams = new URLSearchParams(params)
              newParams.delete('lineage')
              history.replace({ search: newParams.toString() })
              setCultureLineageOpen(false)
            }}
            cultureData={undefined} // Not needed since we have lineageCultures
            parentCultures={lineageCultures}
            openCulture={openCulture}
            selectedTimestamp={observation?.timestamp}
          />
        </Drawer>
      )}
    </div>
  )
}

function LoadingPlaceholder({ plateID, pos }: { plateID: string; pos: string }) {
  // Note: This code was much more fully featured when we had a caching mechanism. When we implemnt
  // one, we should leverage the cached plate data here.
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const history = useHistory()

  return (
    <div className='w-full h-full flex flex-col'>
      {/* Top Bar with Breadcrumbs */}
      <div className='font-text h-[52px] border-b border-grey-lightest px-10 flex items-center shrink-0'>
        <Breadcrumbs plate={{ id: plateID, data: null }} wellPos={pos} />
      </div>

      {/* Main Content Area */}
      <div className='flex gap-6 px-10 h-[calc(100vh-52px)] overflow-hidden'>
        {/* Left Column - Hero and Controls */}
        <div className='flex flex-col flex-1 min-h-0'>
          {/* Title Bar and Toolbar Container - aligned with image */}
          <div className='w-full max-w-[calc(100vh-190px)] xl:max-w-[calc(100vh-200px)] 2xl:max-w-[calc(100vh-220px)]'>
            {/* Title Bar and Toolbar */}
            <div className='flex flex-col gap-4 shrink-0'>
              {/* Title Bar */}
              <div className='flex items-center justify-between h-[50px] mt-5'>
                <div className='flex items-center gap-6'>
                  <span className='font-title text-grey-dark'>Well {pos}</span>

                  <Skeleton className='h-8 w-24' />
                </div>

                <LateralNavControls
                  currentWell={pos}
                  availableWells={[pos]}
                  tinyMicroplateFormat={null}
                  onNavigate={toWell => {
                    if (toWell !== pos) {
                      history.push(
                        `/plate/${plateID}/well/${toWell}?${params.toString()}`,
                      )
                    }
                  }}
                />
              </div>

              {/* Hero Toolbar */}
              <div className='h-[18px] mb-2.25'>
                <HeroToolbar loading />
              </div>
            </div>
          </div>

          {/* Hero/Montage */}
          <div className='w-full max-w-[calc(100vh-190px)] xl:max-w-[calc(100vh-200px)] 2xl:max-w-[calc(100vh-220px)] aspect-square'>
            <MontageHeroLoadingPlaceholder plateDims={undefined} />
          </div>
        </div>

        {/* Right Column - Sidebar Placeholder */}
        <div
          className={cn(
            'w-[360px] xl:w-[540px] 2xl:w-[640px] shrink-0 pt-[113px] overflow-y-auto',
          )}
        >
          <MetadataSidebar className='w-full'>
            <MetadataItem label='First Checked In'>
              <Skeleton className='h-4 w-32' />
            </MetadataItem>

            <MetadataItem label='Parent Culture'>
              <Skeleton className='h-4 w-20' />
            </MetadataItem>

            <MetadataItem label='Cell Line'>
              <Skeleton className='h-8 w-full' />
            </MetadataItem>

            <MetadataItem label='Cell Line Lot'>
              <Skeleton className='h-8 w-full' />
            </MetadataItem>

            <MetadataItem label='Passage Number'>
              <Skeleton className='h-8 w-full' />
            </MetadataItem>

            <div className='mt-4'>
              <Skeleton className='h-10 w-full' />
            </div>
          </MetadataSidebar>
        </div>
      </div>
    </div>
  )
}

function LateralNavControls({
  currentWell,
  availableWells,
  tinyMicroplateFormat,
  onNavigate,
  onHoverWell,
}: {
  currentWell: string
  availableWells: string[]
  tinyMicroplateFormat: supportedPlateFormats | null
  onNavigate: (toWell: string) => void
  onHoverWell?: (well: string) => void
}) {
  const availableWellsSet = useMemo(() => new Set(availableWells), [availableWells])
  const nextWellFor = useWellNavOrdering(currentWell, availableWells)
  const [hoveredWell, setHoveredWell] = useState<string | null>(null)

  // If the plate only has a single well, navigation does not make sense. Hide controls.
  if (availableWells.length <= 1) {
    return null
  }

  return (
    <div
      className='flex items-center text-grey-dark group'
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {
        // For visual stability, we keep showing the label regardless if the
        // user is hovering the minimap or the compass. But, to make it clear
        // that unavailable wells cannot be navigated to, we temporarily hide
        // the label if hovering over an unavailable well.
        !hoveredWell || availableWellsSet.has(hoveredWell) ? (
          <div
            className={cx(
              'font-subtext font-semibold mx-1 hidden group-hover:block',
              (!hoveredWell || hoveredWell === currentWell) && 'text-accent',
            )}
          >
            {hoveredWell ?? currentWell}
          </div>
        ) : null
      }

      {tinyMicroplateFormat ? (
        <TinyMicroplate
          className='!border-none !bg-transparent'
          plateFormat={tinyMicroplateFormat}
          highlights={[
            {
              colorRgbFn: (row: number, col: number) => {
                const well = convertWellCoordsToWellName(row, col)
                if (well === currentWell) {
                  return '#2cb1bc' // Accent
                }
                if (availableWellsSet.has(well)) {
                  return '#ccc' // Grey Light
                }
                return '#eaeaea' // Grey Lightest
              },
            },
          ]}
          onClickCell={(rowIndex, colIndex) => {
            const well = convertWellCoordsToWellName(rowIndex, colIndex)
            if (availableWellsSet.has(well)) {
              onNavigate(well)
            }
          }}
          onMouseOverCell={(rowIndex, colIndex) => {
            const well = convertWellCoordsToWellName(rowIndex, colIndex)
            setHoveredWell(well)
            // Prefetch well data on hover if available
            if (onHoverWell && availableWellsSet.has(well)) {
              onHoverWell(well)
            }
          }}
          onMouseLeave={() => {
            setHoveredWell(null)
          }}
        />
      ) : null}

      <div className='ml-4'>
        <LateralNavCompass
          onNavigate={direction => {
            onNavigate(nextWellFor(direction))
          }}
          onHover={direction => {
            if (onHoverWell) {
              const targetWell = nextWellFor(direction)
              if (targetWell !== currentWell) {
                onHoverWell(targetWell)
              }
            }
          }}
        />
      </div>
    </div>
  )
}

/**
 * Return a function that returns the next well in a particular 2D direction.
 *
 * If there are no available wells, the returned function always returns the
 * current well.
 */
function useWellNavOrdering(
  currentWell: string,
  availableWells: string[],
): (dir: LateralNavDirection) => string {
  const orderings = useMemo(() => {
    const availableCoords = availableWells.map(well => convertWellNameToWellCoord(well))

    // Horizontal navigation uses row-major order: [A1, A2, A3, B1, B2, B3]
    const horizontal = availableCoords
      .toSorted((a, b) => a.colIndex - b.colIndex)
      .toSorted((a, b) => a.rowIndex - b.rowIndex)
      .map(({ rowIndex, colIndex }) => convertWellCoordsToWellName(rowIndex, colIndex))

    // Vertical navigation uses column-major order: [A1, B1, A2, B2, A3, B3]
    const vertical = availableCoords
      .toSorted((a, b) => a.rowIndex - b.rowIndex)
      .toSorted((a, b) => a.colIndex - b.colIndex)
      .map(({ rowIndex, colIndex }) => convertWellCoordsToWellName(rowIndex, colIndex))

    return { horizontal, vertical }
  }, [availableWells])

  const movement: { [key in LateralNavDirection]: [string[], number] } = useMemo(
    () => ({
      [LateralNavDirection.UP]: [orderings.vertical, -1],
      [LateralNavDirection.DOWN]: [orderings.vertical, +1],
      [LateralNavDirection.LEFT]: [orderings.horizontal, -1],
      [LateralNavDirection.RIGHT]: [orderings.horizontal, +1],
    }),
    [orderings],
  )

  return useCallback(
    (direction: LateralNavDirection) => {
      const [order, step] = movement[direction]
      if (order.length === 0) {
        return currentWell
      }

      const currentIndex = order.indexOf(currentWell)
      let newIndex = (currentIndex + step) % order.length
      if (newIndex === -1) {
        newIndex = order.length - 1
      }
      return order[newIndex]
    },
    [movement, currentWell],
  )
}

function HeroToolbar({
  observation,
  page,
  loading,
  onNavigate,
}: {
  observation?: WellPage_PastObservation
  page?: { number: number; total: number }
  loading?: boolean
  onNavigate?: (dir: LateralNavDirection.LEFT | LateralNavDirection.RIGHT) => void
}) {
  const time = observation ? dayjs(observation.generated_at) : null
  const isThisYear = time?.year() === dayjs().year()
  const hackathon2025 = useFeatureFlag('hackathon2025')

  const enabledDirections = [
    page != null && page.number > 1 ? LateralNavDirection.LEFT : null,
    page != null && page.number < page.total ? LateralNavDirection.RIGHT : null,
  ].filter(d => d != null)

  return (
    <div className={cs.heroToolbarContent}>
      <div>
        {time ? (
          <>
            {time.format(isThisYear ? 'MMM D' : 'MMM D, YYYY')}&ensp;
            {time.format('h:mm A')} ({time.fromNow()})
          </>
        ) : loading ? (
          <>&nbsp;</>
        ) : hackathon2025 ? (
          'No data yet.'
        ) : (
          'No images yet.'
        )}
      </div>

      <div className='flex items-center gap-2.5'>
        {page?.total ? (
          <span>
            Observation {page.number} of {page.total}
          </span>
        ) : page ? (
          <span>No observations</span>
        ) : null}

        <LateralNavCompass
          axes={LateralNavAxisMode.HORIZONTAL}
          onNavigate={d => {
            onNavigate?.(d as LateralNavDirection.LEFT | LateralNavDirection.RIGHT)
          }}
          enable={enabledDirections}
        />
      </div>
    </div>
  )
}
